import React, { useState, useEffect, useRef } from "react";
import {
  View,
  StyleSheet,
  TouchableOpacity,
  TextInput,
  Alert,
  Image,
  ImageBackground,
  ActivityIndicator,
} from "react-native";
import Text from "@components/AppText";
import EditPhoto from "@assets/svg/Profile/EditPhoto";
import Button from "@components/Button";
import { colors } from "@constants/theme";
import BackArrow from "@assets/svg/BackArrow";
import { actions } from "@reducers/authReducer";
import { actions as configActions } from "@reducers/configReducer";

import ProfileIcon from "@assets/svg/ProfileIcon";
import CloseIcon from "@assets/svg/CloseIcon";

import toast from "@utils/toast";

// success modal

import SuccessModal from "@screens/RejectedCl/Success";
import ImagePicker from "react-native-image-crop-picker";

import ImageModal from "@components/Profile/ImageModal";
import {
  updateProfileImage,
  updateLeaderName,
  uploadImage,
} from "@services/api";
import { useDispatch, useSelector } from "react-redux";

import {
  PROFILE_EVENTS,
  PROFILE_EVENT_SOURCES,
} from "@constants/analyticsEventsCT";
import analytics from "@services/analytics";
import debounce from "@utils/debounce";
import t from "@constants/localization";
import testIds from "@constants/testIds";
import color from "@constants/color";
import { normalize, width } from "../../utils/metrics";

const PATH = "screens/ProfileInfo/EditProfile";

const EditProfile = ({ navigation, route }) => {
  const {
    params: { edit_type = "Name" },
  } = route;
  const {
    name = "",
    user_image,
    phone_number,
  } = useSelector(({ auth: { user } }) => user);

  const [newName, setNewName] = useState(name);
  const [nameErr, setNameErr] = useState(false);
  const isEnabled = newName.trim().length > 0;

  const [showImageModal, setShowImageModal] = useState(false);
  const [imageUrl, setImageUrl] = useState({ url: user_image });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingImage, setLoadingImage] = useState(false);
  const debounceNavigation = debounce(navigation.goBack, 500);
  const updateUrlRef = useRef();
  const cancelAction = useRef();

  updateUrlRef.current = (url) => {
    setImageUrl({ url });
    setLoadingImage(false);
  };

  const dispatch = useDispatch();

  const showToast = (message) => {
    toast.showShortToast(message);
  };

  const Inputs = useRef([]);

  const inputFields = [
    {
      placeholder: t(`${PATH}/typeHere`),
      value: newName,
      error: nameErr,
      // required: true,
      maxLength: 30,
      keyboardType: "default",
      onChangeText: setNewName,
      handleErr: setNameErr,
      check: () => newName,
      errorMessage: t(`${PATH}/pleaseEnterFullName`),
      type: "name",
      label: t(`${PATH}/name`),
    },
  ];

  const [isSuccess, setIsSuccess] = useState(false);

  // open image picker

  const takePhoto = () => {
    setLoadingImage(true);
    ImagePicker.openCamera({
      mediaType: "photo",
      cropping: false,
      includeBase64: false,
      compressImageQuality: 0.4,
    })
      .then(async (image) => {
        // setImageUrl(image.path);
        setImageUrl({ path: image.path });
        setShowImageModal(false);
        return Promise.race([
          getImageUrl(image),
          new Promise((resolve, reject) => {
            cancelAction.current = resolve;
          }),
        ]);
      })
      .then((res) => {
        let url = res;
        if (res === "cancel") {
          url = user_image;
          showToast(t(`${PATH}/imageUploadCancelled`));
        }
        updateUrlRef.current(url);
      })
      .catch((err) => {
        setShowImageModal(false);
        setLoadingImage(false);
        showToast(err.message);
      });
  };

  const getImageUrl = (image) => {
    const data = new FormData();
    data.append("image", {
      uri: image.path,
      type: image.mime,
      name: image.modificationDate,
    });

    return uploadImage(data).then(async ({ data: { url = "" } }) => {
      return url;
    });
  };

  const chooseFromLibrary = () => {
    setLoadingImage(true);
    ImagePicker.openPicker({
      cropping: false,
      includeBase64: false,
      mediaType: "photo",
      compressImageQuality: 0.4,
    })
      .then(async (image) => {
        setImageUrl({ path: image.path });
        setShowImageModal(false);
        return Promise.race([
          getImageUrl(image),
          new Promise((resolve, reject) => {
            cancelAction.current = resolve;
          }),
        ]);
      })
      .then((res) => {
        let url = res;
        if (res === "cancel") {
          url = user_image;
          showToast(t(`${PATH}/imageUploadCancelled`));
        }
        updateUrlRef.current(url);
      })
      .catch((err) => {
        setShowImageModal(false);
        setLoadingImage(false);
        showToast(err.message);
      });
  };

  const removeProfile = () => {
    setShowImageModal(false);
    setImageUrl("");
  };

  const submitChangesHandler = async () => {
    if (hasUnsavedChanges()) {
      analytics.trackEvent(PROFILE_EVENTS.PROFILE_SAVE, {
        section: PROFILE_EVENT_SOURCES.PRIMARY,
      });
      setIsSubmitting(true);
      try {
        if (newName !== "") await updateLeaderName({ name: newName });
        if (imageUrl !== user_image) await updateProfileImage(imageUrl.url);
        dispatch(actions.getUserDetailsByTokenRequest()); // updated overall everywhere
        dispatch(configActions.getAppBootParams()); // reflect changes everywhere
        // setIsSuccess(true);
        setIsSubmitting(false);
        showToast(t(`${PATH}/profileUpdated`));
        debounceNavigation();
      } catch (err) {
        console.warn(err);
        setIsSubmitting(false);
        showToast(err.message);
      }
    }
  };

  const editing = useRef(false);

  const goingBackHandler = () => {
    if (!editing.current) navigation.goBack();
    else {
      return Alert.alert("", t(`${PATH}/cnfmChanges`), [
        {
          text: t(`${PATH}/no`),
          onPress: () => navigation.goBack(),
        },
        {
          text: t(`${PATH}/yes`),
          onPress: submitChangesHandler,
        },
      ]);
    }
  };

  const hasUnsavedChanges = () => {
    let hasChanges = false;
    if ((newName !== name && newName !== "") || imageUrl !== user_image) {
      hasChanges = true;
    }
    return hasChanges;
  };

  return (
    <>
      {edit_type === "Name" ? (
        <View style={{ flex: 1, backgroundColor: "white" }}>
          <View style={styles.topNavContainer}>
            <View style={{ flexDirection: "row", alignItems: "center" }}>
              <TouchableOpacity
                onPress={goingBackHandler}
                style={styles.arrowCont}
              >
                <CloseIcon />
              </TouchableOpacity>
            </View>
          </View>
          <SuccessModal
            message={t(`${PATH}/profileUpdated`)}
            onPress={() => navigation.navigate("ProfileMain")}
            visible={isSuccess}
          />
          <ImageModal
            visible={showImageModal}
            onRequestClose={() => setShowImageModal(false)}
            takePhoto={takePhoto}
            chooseFromLibrary={chooseFromLibrary}
            removeProfile={removeProfile}
          />
          <View style={styles.headingContainer}>
            <Text style={styles.headingText}>{t(`${PATH}/yourName`)}</Text>
          </View>
          <View style={{ marginTop: normalize(30) }}>
            {inputFields.map((el, i) => (
              <InputField
                testnameId={testIds.edit_name}
                placeholder={el.placeholder}
                label={el.label}
                value={el.value}
                error={el.error}
                handleErr={el.handleErr}
                maxLength={el.maxLength}
                type={el.type}
                required={el.required}
                keyboardType={el.keyboardType}
                errorMessage={el.errorMessage}
                onChangeText={el.onChangeText}
                check={el.check}
                onBlur={() => {
                  if (!el.check() && el.required) el.handleErr(true);
                }}
                ref={(el) => {
                  Inputs.current[i] = el;
                }}
                blurOnSubmit={true}
                onSubmitEditing={() => {
                  if (Inputs.current[i + 1]) Inputs.current[i + 1].focus();
                  else Inputs.current[i].blur();
                }}
              />
            ))}
          </View>
          {!/^[a-zA-Z\s]+$/.test(newName) && (
            <Text
              style={{
                color: color.plain_red,
                paddingHorizontal: 20,
                fontSize: 12,
                marginBottom: 10,
              }}
            >
              {t("screens/Auth/EnterName/nameCheck")}
            </Text>
          )}
          <Button
            testID={testIds.save_profile_btn}
            btnText={t(`${PATH}/yes`)}
            buttonFontType="bold"
            fontSize={normalize(20)}
            buttonWidth={width * 0.89}
            disabled={!isEnabled || !/^[a-zA-Z\s]+$/.test(newName)}
            isSubmitting={isSubmitting}
            onPress={submitChangesHandler}
            height={normalize(50)}
            borderRadius={10}
            containerStyle={{
              alignSelf: "center",
              marginVertical: normalize(10),
            }}
          />
        </View>
      ) : (
        <View
          style={{ flex: 1, backgroundColor: "white" }}
          testID={testIds.edit_Profile}
        >
          <View style={styles.topNavContainer}>
            <View style={{ flexDirection: "row", alignItems: "center" }}>
              <TouchableOpacity
                onPress={goingBackHandler}
                style={styles.arrowCont}
              >
                <BackArrow />
              </TouchableOpacity>
              <Text bold fontType="head1">
                {t(`${PATH}/editProfile`)}
              </Text>
            </View>
          </View>
          <SuccessModal
            message={t(`${PATH}/profileUpdated`)}
            onPress={() => navigation.navigate("ProfileMain")}
            visible={isSuccess}
          />
          <ImageModal
            visible={showImageModal}
            onRequestClose={() => setShowImageModal(false)}
            takePhoto={takePhoto}
            chooseFromLibrary={chooseFromLibrary}
            removeProfile={removeProfile}
          />
          <View
            style={{
              alignItems: "center",
              marginTop: normalize(30),
              marginBottom: normalize(20),
            }}
          >
            <View
              style={{
                position: "relative",
                alignItems: "center",
                justifyContent: "center",
                padding: 4,
              }}
            >
              <View style={{ borderRadius: 120, overflow: "hidden" }}>
                <ImageBackground
                  resizeMode="cover"
                  style={{
                    width: normalize(110),
                    height: normalize(110),
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  source={{ uri: imageUrl.path || imageUrl.url }}
                >
                  {loadingImage && (
                    <View
                      style={{
                        position: "absolute",
                        right: "40%",
                        top: "40%",
                        backgroundColor: "#ffffff66",
                        borderRadius: 15,
                        borderColor: "#e6e6e6",
                        borderWidth: 1,
                        width: 30,
                        height: 30,
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <View
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <ActivityIndicator
                          size="large"
                          color={colors.primary}
                        />
                      </View>
                      <TouchableOpacity
                        style={{}}
                        onPress={() => {
                          cancelAction.current("cancel");
                        }}
                      >
                        <CloseIcon />
                      </TouchableOpacity>
                    </View>
                  )}
                </ImageBackground>
              </View>
              {!loadingImage && (
                <TouchableOpacity
                  testID={testIds.edit_Pic}
                  onPress={() => setShowImageModal(true)}
                  style={{
                    position: "absolute",
                    bottom: 0,
                    right: 0,
                    zIndex: 3,
                  }}
                >
                  <View>
                    <EditPhoto />
                  </View>
                </TouchableOpacity>
              )}
            </View>
          </View>
          <View
            style={{ marginTop: normalize(30) }}
            testID={testIds.name_Input}
          >
            {inputFields.map((el, i) => (
              <InputField
                placeholder={el.placeholder}
                testnameId={testIds.edit_name}
                label={el.label}
                value={el.value}
                error={el.error}
                handleErr={el.handleErr}
                maxLength={el.maxLength}
                type={el.type}
                required={el.required}
                keyboardType={el.keyboardType}
                errorMessage={el.errorMessage}
                onChangeText={el.onChangeText}
                check={el.check}
                onBlur={() => {
                  if (!el.check() && el.required) el.handleErr(true);
                }}
                ref={(el) => {
                  Inputs.current[i] = el;
                }}
                blurOnSubmit={true}
                onSubmitEditing={() => {
                  if (Inputs.current[i + 1]) Inputs.current[i + 1].focus();
                  else Inputs.current[i].blur();
                }}
              />
            ))}

            <View style={{ marginBottom: normalize(20) }}>
              <Text
                testID={testIds.mobile_Number}
                style={{
                  fontSize: normalize(14),
                  lineHeight: normalize(16),
                  marginBottom: normalize(7),
                  color: "rgba(0, 0, 0, 0.36)",
                  marginLeft: width * 0.11 * 0.5,
                }}
              >
                {t(`${PATH}/mobileNo`)}
              </Text>
              <View
                style={{
                  width: width * 0.89,
                  borderColor: "rgba(0, 0, 0, 0.2)",
                  borderWidth: 1,
                  alignSelf: "center",
                  borderRadius: normalize(10),
                  paddingVertical: normalize(13),
                  paddingLeft: normalize(10),
                  flexDirection: "row",
                  alignItems: "center",
                  backgroundColor: "rgba(0, 0, 0, 0.1)",
                }}
              >
                <View style={{ paddingBottom: normalize(1) }}>
                  <Text
                    style={{
                      fontSize: normalize(16),
                      lineHeight: normalize(24),
                      color: "rgba(0, 0, 0, 0.56)",
                    }}
                  >
                    +91 {phone_number}
                  </Text>
                </View>
              </View>
            </View>
          </View>
          <Button
            testID={testIds.save_profile_btn}
            btnText={t(`${PATH}/yes`)}
            buttonFontType="bold"
            fontSize={normalize(20)}
            buttonWidth={width * 0.89}
            disabled={loadingImage}
            // disabled={!hasUnsavedChanges()}
            isSubmitting={isSubmitting}
            onPress={submitChangesHandler}
            height={normalize(50)}
            borderRadius={10}
            containerStyle={{
              alignSelf: "center",
              marginTop: "auto",
              marginVertical: normalize(10),
            }}
          />
        </View>
      )}
    </>
  );
};

export default EditProfile;

const InputField = React.forwardRef(
  (
    {
      testnameId = "",
      placeholder,
      keyboardType,
      value,
      maxLength,
      onChangeText,
      blurOnSubmit,
      onSubmitEditing,
      error,
      errorMessage,
      onBlur,
      label,
      type,
      handleErr,
      check,
      style = {},
    },
    ref,
  ) => {
    useEffect(() => {
      if (check()) handleErr(null);
      if (value && !check() && error) handleErr(true);
    });
    return (
      <View>
        <View style={styles.floatingInputContainer}>
          <Text style={styles.label}>{label}</Text>
          <TextInput
            testID={testnameId}
            placeholder={placeholder}
            selectionColor="black"
            keyboardType={keyboardType}
            value={value}
            ref={ref}
            maxLength={maxLength}
            onChangeText={(val) => {
              onChangeText(val);
            }}
            blurOnSubmit={blurOnSubmit}
            onSubmitEditing={onSubmitEditing}
            onBlur={onBlur}
            ref={ref}
            style={styles.textinput}
          />
        </View>
        <Text bold style={styles.error}>
          {error && errorMessage}
        </Text>
      </View>
    );
  },
);

const styles = StyleSheet.create({
  floatingInputContainer: {
    margin: normalize(16),
    marginBottom: 0,
    borderWidth: 0.5,
    borderColor: "rgba(0, 0, 0, 0.5)",
    justifyContent: "center",
    borderRadius: 6,
    position: "relative",
    height: normalize(56),
    paddingHorizontal: 10,
  },
  label: {
    backgroundColor: "white",
    padding: 5,
    borderRadius: 200,
    position: "absolute",
    top: -20,
    left: 10,
    color: "rgba(0, 0, 0, 0.56)",
    fontSize: normalize(14),
    fontFamily: "Mukta",
  },
  textinput: {
    color: "black",
    fontSize: normalize(16),
    lineHeight: normalize(24),
    flex: 1,
  },
  error: {
    marginLeft: width * (1 - 0.87) * 0.5,
    marginTop: 2,
    color: colors.errorRed,
  },
  topNavContainer: {
    flexDirection: "row",
    paddingVertical: normalize(18),
    paddingHorizontal: normalize(11),
    backgroundColor: colors.white,
    alignItems: "center",
    alignContent: "space-around",
    justifyContent: "space-between",
  },
  arrowCont: {
    marginRight: normalize(20),
  },
  headingContainer: {
    margin: 20,
  },
  headingText: {
    fontFamily: "NotoSansDevanagari-Bold",
    fontSize: 24,
    lineHeight: 34,
    color: "rgba(0, 0, 0, 0.86)",
  },
});
